import { useCallback, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { WhyDisabled } from "@/components/ui/disabled-reason";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import {
  Beaker,
  ChevronDown,
  ChevronRight,
  Clock,
  Download,
  Globe2,
  Loader2,
  Search,
  Users,
  Wrench,
  X,
} from "lucide-react";
import { useGapFillStore } from "@/stores/gap-fill-store";
import type { Gap, GapKind } from "@/types/audit";

interface ResearchGap {
  title: string;
  description: string;
  evidence: string;
  suggested_directions: string[];
}

interface ClusterIntersection {
  clusters: string[];
  opportunity: string;
  rationale: string;
}

export interface GapAnalysisResult {
  gaps: ResearchGap[];
  intersections: ClusterIntersection[];
  synthesis: string;
  cluster_count: number;
  paper_count: number;
  total_corpus_papers?: number;
  cluster_labels?: string[];
  error?: string | null;
  typed_gaps?: Gap[];
  audit_id?: string;
}

type KindIcon = typeof Beaker;
const KIND_META: Record<
  GapKind,
  { icon: KindIcon; color: string; label: string }
> = {
  "missing-evidence-type": {
    icon: Beaker,
    color: "#a6e3a1",
    label: "missing-evidence-type",
  },
  "missing-method": {
    icon: Wrench,
    color: "#89b4fa",
    label: "missing-method",
  },
  "missing-domain": {
    icon: Globe2,
    color: "#cba6f7",
    label: "missing-domain",
  },
  "missing-population": {
    icon: Users,
    color: "#f9e2af",
    label: "missing-population",
  },
  "missing-temporal": {
    icon: Clock,
    color: "#f38ba8",
    label: "missing-temporal",
  },
};

function TypedGapCard({
  gap,
  auditId,
  onFindPapers,
  onAddAsAngle,
  addAngleDisabledReason,
}: {
  gap: Gap;
  auditId?: string;
  onFindPapers?: (gap: Gap, auditId: string) => void;
  onAddAsAngle?: (gap: Gap) => void;
  addAngleDisabledReason?: string;
}) {
  const meta = KIND_META[gap.kind];
  const Icon = meta.icon;
  const canFindPapers = Boolean(auditId && onFindPapers);

  // Per-card spinner: subscribe with fine-grained selectors so only the
  // active card re-renders on store updates. kind+tag is the de facto
  // composite identity used by the audit pipeline.
  const isThisGapRunning = useGapFillStore(
    (s) =>
      s.isPending && s.gap?.kind === gap.kind && s.gap?.tag === gap.tag,
  );
  const isAnyGapRunning = useGapFillStore((s) => s.isPending);
  const stepLabel = useGapFillStore((s) =>
    s.isPending && s.gap?.kind === gap.kind && s.gap?.tag === gap.tag
      ? s.stepLabel
      : null,
  );
  return (
    <div
      className="rounded-md border border-border bg-card/50 px-3 py-3"
      data-testid={`typed-gap-card-${gap.kind}`}
    >
      <div className="mb-2 flex items-center gap-2">
        <Icon className="size-3.5" style={{ color: meta.color }} />
        <span
          className="rounded px-1.5 py-0.5 font-mono text-xs"
          style={{
            backgroundColor: `${meta.color}20`,
            color: meta.color,
          }}
        >
          {meta.label}
        </span>
        <span className="text-xs text-muted-foreground">·</span>
        <span className="text-xs font-medium text-foreground">{gap.tag}</span>
      </div>
      <p className="text-sm text-foreground/90">{gap.description}</p>
      {gap.suggested_queries.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1">
          {gap.suggested_queries.map((q, i) => (
            <span
              key={i}
              className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs text-muted-foreground"
            >
              {q}
            </span>
          ))}
        </div>
      )}
      {onAddAsAngle && (
        <div className="mt-3 flex justify-end">
          <WhyDisabled reason={addAngleDisabledReason}>
            <Button
              size="xs"
              variant="ghost"
              onClick={() => onAddAsAngle(gap)}
              disabled={Boolean(addAngleDisabledReason)}
              data-testid={`add-angle-${gap.kind}`}
            >
              Add as an angle
            </Button>
          </WhyDisabled>
        </div>
      )}
      {canFindPapers &&
        (isThisGapRunning ? (
          <div className="mt-3 flex items-center justify-end gap-1.5 text-xs italic text-muted-foreground">
            <Loader2 className="size-3 animate-spin" />
            {stepLabel || "Searching…"}
          </div>
        ) : isAnyGapRunning ? null : (
          <div className="mt-3 flex justify-end">
            <Button
              size="xs"
              variant="ghost"
              onClick={() => onFindPapers!(gap, auditId!)}
              data-testid={`find-papers-${gap.kind}`}
            >
              <Search className="mr-1 size-3" />
              Find papers
            </Button>
          </div>
        ))}
    </div>
  );
}

interface Props {
  data: GapAnalysisResult;
  onDismiss?: () => void;
  /** Wires the per-gap "Find papers" action. If absent (or audit has
   * no audit_id — legacy audits), the action is hidden. */
  onFindPapers?: (gap: Gap, auditId: string) => void;
  /** Facets ring: opens the Add-an-angle sheet prefilled from this gap. */
  onAddAsAngle?: (gap: Gap) => void;
  addAngleDisabledReason?: string;
}

function resultToMarkdown(data: GapAnalysisResult): string {
  const lines: string[] = [];
  lines.push("# Corpus Coverage Audit");
  lines.push("");
  const coverageNote = data.total_corpus_papers
    ? `${data.paper_count} of ${data.total_corpus_papers} papers analyzed`
    : `${data.paper_count} papers analyzed`;
  lines.push(`*${data.cluster_count} clusters, ${coverageNote}*`);
  if (data.cluster_labels?.length) {
    lines.push("");
    lines.push(`Clusters: ${data.cluster_labels.join(", ")}`);
  }
  lines.push("");

  if (data.synthesis) {
    lines.push("## Coverage Assessment");
    lines.push("");
    lines.push(data.synthesis);
    lines.push("");
  }

  if (data.typed_gaps && data.typed_gaps.length > 0) {
    lines.push("## Coverage Gaps");
    lines.push("");
    for (const gap of data.typed_gaps) {
      lines.push(`### [${gap.kind}] ${gap.tag}`);
      lines.push("");
      lines.push(gap.description);
      lines.push("");
      if (gap.suggested_queries.length > 0) {
        lines.push("**Suggested queries:**");
        lines.push("");
        for (const q of gap.suggested_queries) {
          lines.push(`- \`${q}\``);
        }
        lines.push("");
      }
    }
  } else if (data.gaps.length > 0) {
    lines.push("## Coverage Gaps");
    lines.push("");
    for (const gap of data.gaps) {
      lines.push(`### ${gap.title}`);
      lines.push("");
      lines.push(gap.description);
      lines.push("");
      lines.push(`**Evidence:** ${gap.evidence}`);
      lines.push("");
      if (gap.suggested_directions.length > 0) {
        lines.push("**Suggested Directions:**");
        lines.push("");
        for (const d of gap.suggested_directions) {
          lines.push(`- ${d}`);
        }
        lines.push("");
      }
    }
  }

  if (data.intersections.length > 0) {
    lines.push("## Cross-Cluster Opportunities");
    lines.push("");
    for (const inter of data.intersections) {
      lines.push(`### ${inter.clusters.join(" + ")}`);
      lines.push("");
      lines.push(inter.opportunity);
      lines.push("");
      lines.push(`**Rationale:** ${inter.rationale}`);
      lines.push("");
    }
  }

  return lines.join("\n");
}

export function GapAnalysisPanel({ data, onDismiss, onFindPapers, onAddAsAngle, addAngleDisabledReason }: Props) {
  const [expanded, setExpanded] = useState(true);

  const handleExport = useCallback(() => {
    const md = resultToMarkdown(data);
    const blob = new Blob([md], { type: "text/markdown" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "gap-analysis.md";
    a.click();
    URL.revokeObjectURL(url);
  }, [data]);

  if (data.error) {
    return (
      <div className="rounded-md border border-destructive p-4">
        <p className="text-sm text-destructive">
          Gap analysis failed: {data.error}
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-md border" data-testid="gap-analysis-panel">
      {/* Collapsible header */}
      <div className="flex items-center justify-between px-4 py-3">
        <button
          type="button"
          className="flex items-center gap-2 bg-transparent p-0 text-left"
          onClick={() => setExpanded((e) => !e)}
        >
          {expanded ? (
            <ChevronDown className="size-4 text-muted-foreground" />
          ) : (
            <ChevronRight className="size-4 text-muted-foreground" />
          )}
          <div>
            <h3 className="text-sm font-semibold">Corpus Coverage Audit</h3>
            <p className="text-xs text-muted-foreground">
              {data.cluster_count} clusters
              {data.cluster_labels?.length ? ` (${data.cluster_labels.join(", ")})` : ""}
              {" · "}
              {data.total_corpus_papers
                ? `${data.paper_count} of ${data.total_corpus_papers} papers analyzed`
                : `${data.paper_count} papers analyzed`}
              {data.cluster_count > 0 &&
                ` · top ${Math.round(data.paper_count / data.cluster_count)} papers audited in each of the ${data.cluster_count} clusters`}
            </p>
          </div>
        </button>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="xs"
            onClick={handleExport}
            title="Export as Markdown"
          >
            <Download className="size-3.5" />
          </Button>
          {onDismiss && (
            <Button
              variant="ghost"
              size="xs"
              onClick={onDismiss}
              aria-label="Dismiss gap analysis"
            >
              <X className="size-3.5" />
            </Button>
          )}
        </div>
      </div>

      {/* Collapsible content */}
      {expanded && (
        <div className="space-y-4 px-4 pb-4">
          {/* Coverage Assessment */}
          {data.synthesis && (
            <div className="space-y-2">
              <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Coverage Assessment
              </h4>
              <p className="text-sm leading-relaxed">{data.synthesis}</p>
            </div>
          )}

          {/* Coverage Gaps */}
          {data.typed_gaps && data.typed_gaps.length > 0 ? (
            <div className="space-y-3">
              <Separator />
              <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Coverage Gaps
              </h4>
              <div className="space-y-2">
                {data.typed_gaps.map((gap, i) => (
                  <TypedGapCard
                    key={i}
                    gap={gap}
                    auditId={data.audit_id}
                    onFindPapers={onFindPapers}
                    onAddAsAngle={onAddAsAngle}
                    addAngleDisabledReason={addAngleDisabledReason}
                  />
                ))}
              </div>
            </div>
          ) : (
            data.gaps.length > 0 && (
              <div className="space-y-3">
                <Separator />
                <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Coverage Gaps
                </h4>
                {data.gaps.map((gap, i) => (
                  <Card key={i}>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm">{gap.title}</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2 text-xs">
                      <p>{gap.description}</p>
                      <p className="text-muted-foreground">
                        <span className="font-medium">Evidence:</span>{" "}
                        {gap.evidence}
                      </p>
                      {gap.suggested_directions.length > 0 && (
                        <div>
                          <span className="font-medium">Directions:</span>
                          <ul className="ml-4 mt-1 list-disc">
                            {gap.suggested_directions.map((d, j) => (
                              <li key={j}>{d}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )
          )}

          {/* Cross-Cluster Opportunities */}
          {data.intersections.length > 0 && (
            <div className="space-y-3">
              <Separator />
              <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Cross-Cluster Opportunities
              </h4>
              {data.intersections.map((inter, i) => (
                <Card key={i}>
                  <CardContent className="space-y-2 pt-4 text-xs">
                    <div className="flex flex-wrap gap-1">
                      {inter.clusters.map((c) => (
                        <Badge key={c} variant="secondary" className="text-xs">
                          {c}
                        </Badge>
                      ))}
                    </div>
                    <p>{inter.opportunity}</p>
                    <p className="text-muted-foreground">
                      <span className="font-medium">Rationale:</span>{" "}
                      {inter.rationale}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
